// apps/patient/src/components/profile/PatientProfileView.tsx

import {
  User,
  Phone,
  Mail,
  MapPin,
  Bell,
  Shield,
  Calendar,
  AlertCircle,
  Users,
  MessageSquare,
  Dumbbell,
  ChevronRight
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';

interface PatientProfileViewProps {
  user: any;
}

export function PatientProfileView({ user }: PatientProfileViewProps) {
  const navigate = useNavigate();
  const patient = user?.patient;

  const formatPhoneNumber = (phone: string) => {
    if (!phone) return 'Not provided';
    const cleaned = phone.replace(/\D/g, '');
    if (cleaned.length === 10) {
      return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
    }
    return phone;
  };

  const formatAddress = (address: any) => {
    if (!address) return 'Not provided';
    const parts = [
      address.line1,
      address.line2,
      `${address.city}, ${address.state} ${address.zip}`
    ].filter(Boolean);
    return parts.join('\n');
  };

  return (
    <div className="divide-y divide-gray-200">
      {/* Basic Information */}
      <div className="p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
          <User className="w-5 h-5 mr-2 text-gray-400" />
          Basic Information
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="text-sm text-gray-500">Full Name</label>
            <p className="text-gray-900 font-medium">
              {user.first_name} {user.last_name}
            </p>
          </div>
          <div>
            <label className="text-sm text-gray-500">Email</label>
            <p className="text-gray-900">{user.email}</p>
          </div>
          {patient?.birthday && (
            <div>
              <label className="text-sm text-gray-500">Date of Birth</label>
              <p className="text-gray-900">{patient.birthday}</p>
            </div>
          )}
          {patient?.gender && (
            <div>
              <label className="text-sm text-gray-500">Gender</label>
              <p className="text-gray-900 capitalize">{patient.gender}</p>
            </div>
          )}
        </div>
      </div>

      {/* Contact Information */}
      <div className="p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
          <Phone className="w-5 h-5 mr-2 text-gray-400" />
          Contact Information
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="text-sm text-gray-500">Phone Number</label>
            <p className="text-gray-900">{formatPhoneNumber(user.phone || patient?.phone)}</p>
          </div>
          <div>
            <label className="text-sm text-gray-500">Preferred Contact Method</label>
            <p className="text-gray-900 capitalize">
              {patient?.preferred_contact_method || 'Not specified'}
            </p>
          </div>
        </div>
      </div>

      {/* Address */}
      <div className="p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
          <MapPin className="w-5 h-5 mr-2 text-gray-400" />
          Address
        </h3>
        <div className="whitespace-pre-line text-gray-900">
          {formatAddress(patient?.address)}
        </div>
      </div>

      {/* Emergency Contacts */}
      {patient?.emergency_contacts && patient.emergency_contacts.length > 0 && (
        <div className="p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
            <AlertCircle className="w-5 h-5 mr-2 text-gray-400" />
            Emergency Contacts
          </h3>
          <div className="space-y-4">
            {patient.emergency_contacts.map((contact: any, index: number) => (
              <div key={index} className="border-l-4 border-primary/20 pl-4">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="font-medium text-gray-900">
                      {contact.name}
                      {contact.is_primary && (
                        <span className="ml-2 text-xs bg-primary/10 text-primary px-2 py-1 rounded">
                          Primary
                        </span>
                      )}
                    </p>
                    <p className="text-sm text-gray-600">{contact.relationship}</p>
                    <p className="text-sm text-gray-600">{formatPhoneNumber(contact.phone)}</p>
                    {contact.email && (
                      <p className="text-sm text-gray-600">{contact.email}</p>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Insurance Information */}
      {patient?.insurance && patient.insurance.length > 0 && (
        <div className="p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
            <Shield className="w-5 h-5 mr-2 text-gray-400" />
            Insurance Information
          </h3>
          <div className="space-y-4">
            {patient.insurance.map((ins: any, index: number) => (
              <div key={index} className="border-l-4 border-primary/20 pl-4">
                <div>
                  <p className="font-medium text-gray-900">
                    {ins.payer_name}
                    {ins.is_primary && (
                      <span className="ml-2 text-xs bg-primary/10 text-primary px-2 py-1 rounded">
                        Primary
                      </span>
                    )}
                  </p>
                  <div className="grid grid-cols-2 gap-2 mt-2 text-sm">
                    <div>
                      <span className="text-gray-500">Member ID:</span>
                      <span className="ml-2 text-gray-900">{ins.member_id}</span>
                    </div>
                    {ins.group_number && (
                      <div>
                        <span className="text-gray-500">Group:</span>
                        <span className="ml-2 text-gray-900">{ins.group_number}</span>
                      </div>
                    )}
                    {ins.plan_name && (
                      <div className="col-span-2">
                        <span className="text-gray-500">Plan:</span>
                        <span className="ml-2 text-gray-900">{ins.plan_name}</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Notifications & Security */}
      <div className="p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
          <Bell className="w-5 h-5 mr-2 text-gray-400" />
          Notifications & Security
        </h3>
        <div className="space-y-3">
          <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
            <div className="flex items-center">
              <Mail className="w-5 h-5 mr-3 text-gray-400" />
              <span className="text-gray-700">Email Notifications</span>
            </div>
            <span className={`px-3 py-1 rounded-full text-sm ${
              user.patient?.email_preferences ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'
            }`}>
              {user.patient?.email_preferences ? 'Enabled' : 'Disabled'}
            </span>
          </div>
          
          <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
            <div className="flex items-center">
              <MessageSquare className="w-5 h-5 mr-3 text-gray-400" />
              <span className="text-gray-700">Text Message Notifications</span>
            </div>
            <span className={`px-3 py-1 rounded-full text-sm ${
              user.patient?.sms_preferences ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'
            }`}>
              {user.patient?.sms_preferences ? 'Enabled' : 'Disabled'}
            </span>
          </div>
          
          <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
            <div className="flex items-center">
              <Shield className="w-5 h-5 mr-3 text-gray-400" />
              <span className="text-gray-700">Biometric Authentication</span>
            </div>
            <span className={`px-3 py-1 rounded-full text-sm ${
              user.biometric_mfa ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'
            }`}>
              {user.biometric_mfa ? 'Enabled' : 'Disabled'}
            </span>
          </div>
        </div>
      </div>

      {/* Workout Settings */}
      <div className="p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
          <Dumbbell className="w-5 h-5 mr-2 text-gray-400" />
          Workout Settings
        </h3>
        <div className="space-y-3">
          <button
            onClick={() => navigate('/settings/locations')}
            className="w-full flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
          >
            <div className="flex items-center">
              <MapPin className="w-5 h-5 mr-3 text-gray-400" />
              <div className="text-left">
                <span className="text-gray-700 font-medium">My Workout Locations</span>
                <p className="text-sm text-gray-500">Manage gyms, home equipment, and more</p>
              </div>
            </div>
            <ChevronRight className="w-5 h-5 text-gray-400" />
          </button>
        </div>
      </div>

      {/* Care Team */}
      {patient?.care_team && patient.care_team.length > 0 && (
        <div className="p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
            <Users className="w-5 h-5 mr-2 text-gray-400" />
            Care Team
          </h3>
          <div className="space-y-3">
            {patient.care_team.filter((member: any) => member.is_active).map((member: any, index: number) => (
              <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div>
                  <p className="font-medium text-gray-900">{member.provider_name || 'Provider'}</p>
                  <p className="text-sm text-gray-600 capitalize">{member.role}</p>
                  {member.specialty && (
                    <p className="text-xs text-gray-500">{member.specialty}</p>
                  )}
                </div>
                {member.role === 'primary' && (
                  <span className="text-xs bg-primary/10 text-primary px-2 py-1 rounded">
                    Primary Provider
                  </span>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}